import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert, Box, Button, Chip, Dialog, DialogContent, DialogTitle, List, ListItem, ListItemText, Stack,
  Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { useServices } from '@/lib/ServicesContext';
import { warszawaNaUtc, zPolaDaty } from '@/lib/tresci/czasWarszawa';
import { DOPISEK_WYNIK_NIEZNANY, komunikatBledu, wWarszawie, wynikNieznany } from '@/lib/tresci/tresciUi';
import {
  ETYKIETA_STANU, tresciApi, type PodgladOdbiorcy, type PozycjaPaska, type UczenKatalogu, type WpisHistorii,
} from '@/lib/api/tresci';

function odbiorcy(p: PozycjaPaska): string {
  switch (p.scope) {
    case 'CITY': return `${p.cityIds.length} miast`;
    case 'SCHOOL': return `${p.schoolIds.length} szkół`;
    case 'USER': return `${p.userIds.length} uczniów`;
    default: return 'wszyscy';
  }
}

export interface PozycjePaskaProps {
  /** Zmiana licznika = przeładuj listę (po zapisie w formularzu). */
  odswiez: number;
  mozePublikowac: boolean;
  /** Serwer CC potwierdził drogę zapisu (`/stan`). Bez niej akcje są widoczne, ale wyłączone. */
  zapisDostepny: boolean;
}

/** Lista pozycji paska ze stanem efektywnym, akcjami i historią (autor każdej zmiany). */
export function PozycjePaska({ odswiez, mozePublikowac, zapisDostepny }: PozycjePaskaProps) {
  const { api } = useServices();
  const [pozycje, setPozycje] = useState<PozycjaPaska[] | null>(null);
  // Dwa różne błędy: listy (odczyt) i akcji (zapis). Udany odczyt po akcji nie może skasować błędu akcji.
  const [bladListy, setBladListy] = useState<string | null>(null);
  const [blad, setBlad] = useState<string | null>(null);
  const [historia, setHistoria] = useState<{ id: number; wpisy: WpisHistorii[] } | null>(null);
  const [licznik, setLicznik] = useState(0);
  const [odczytano, setOdczytano] = useState<Date | null>(null);

  useEffect(() => {
    tresciApi.paski(api)
      .then((p) => { setPozycje(p); setOdczytano(new Date()); setBladListy(null); })
      .catch((e: unknown) => setBladListy(komunikatBledu(e)));
  }, [api, odswiez, licznik]);

  /*
   * Po udanej akcji (Opublikuj, Wyłącz) lista czyta się ponownie z systemu głównego — bez F5
   * (OWNER 10.10). Po ODRZUCONEJ akcji (4xx) lista zostaje bez zmian, a powód jest w alercie (B8 z recenzji
   * #833: odmowa nie może wyglądać na zmianę). Wynik NIEZNANY (sieć, przekroczony czas, 5xx) to nie odmowa:
   * lista czyta się ponownie, a alert mówi, że operacja mogła się wykonać.
   */
  const akcja = useCallback(async (fn: () => Promise<unknown>) => {
    try {
      await fn();
      setBlad(null);
      setLicznik((x) => x + 1);
    } catch (e) {
      const nieznany = wynikNieznany(e);
      setBlad(komunikatBledu(e) + (nieznany ? DOPISEK_WYNIK_NIEZNANY : ''));
      if (nieznany) setLicznik((x) => x + 1);
    }
  }, []);

  return (
    <Box>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 1 }}>
        <Typography variant="caption" sx={{ color: 'text.secondary' }} data-testid="paski-odczytano">
          {odczytano ? `Odczytano ${odczytano.toLocaleTimeString('pl-PL', { timeZone: 'Europe/Warsaw' })}` : ''}
        </Typography>
        <Button size="small" onClick={() => setLicznik((x) => x + 1)}>Odśwież</Button>
      </Stack>
      {bladListy ? <Alert severity="error" role="alert" sx={{ mb: 1 }}>Nie udało się odczytać pozycji paska: {bladListy}</Alert> : null}
      {blad ? <Alert severity="error" role="alert" sx={{ mb: 1 }}>{blad}</Alert> : null}
      {pozycje === null && !bladListy ? <Typography variant="body2">Wczytywanie…</Typography> : null}
      {pozycje && pozycje.length === 0 ? <Typography variant="body2">Brak pozycji — aplikacja pokazuje zwykły pasek.</Typography> : null}
      {pozycje && pozycje.length > 0 ? (
        <Table size="small" aria-label="Pozycje paska">
          <TableHead>
            <TableRow>
              <TableCell>Stan</TableCell><TableCell>Treść</TableCell><TableCell>Zakres</TableCell>
              <TableCell>Od (Warszawa)</TableCell><TableCell>Do</TableCell><TableCell>Priorytet</TableCell>
              <TableCell>Zmienione przez</TableCell><TableCell>Akcje</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {pozycje.map((p) => (
              <TableRow key={p.id} data-testid={`pozycja-${p.id}`}>
                <TableCell><Chip size="small" label={ETYKIETA_STANU[p.effectiveStatus] ?? p.effectiveStatus} /></TableCell>
                <TableCell sx={{ maxWidth: 280 }}>{p.message}</TableCell>
                <TableCell>{p.scope} · {odbiorcy(p)}</TableCell>
                <TableCell>{wWarszawie(p.startsAt)}</TableCell>
                <TableCell>{p.endsAt ? wWarszawie(p.endsAt) : 'bez końca'}</TableCell>
                <TableCell>{p.priority}</TableCell>
                <TableCell>{p.updatedBy ?? '—'}</TableCell>
                <TableCell>
                  <Stack direction="row" spacing={1}>
                    {mozePublikowac && p.status === 'DRAFT'
                      ? <Button size="small" disabled={!zapisDostepny} onClick={() => akcja(() => tresciApi.opublikujPasek(api, p.id))}>Opublikuj</Button> : null}
                    {mozePublikowac && p.status === 'PUBLISHED'
                      ? <Button size="small" color="warning" disabled={!zapisDostepny} onClick={() => akcja(() => tresciApi.wylaczPasek(api, p.id))}>Wyłącz</Button> : null}
                    <Button size="small" onClick={() => tresciApi.historiaPaska(api, p.id)
                      .then((w) => setHistoria({ id: p.id, wpisy: w })).catch((e: unknown) => setBlad(komunikatBledu(e)))}>Historia</Button>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : null}

      <Dialog open={historia !== null} onClose={() => setHistoria(null)} fullWidth>
        <DialogTitle>Historia pozycji #{historia?.id}</DialogTitle>
        <DialogContent>
          <List dense>
            {historia?.wpisy.map((h) => (
              <ListItem key={h.id}>
                <ListItemText primary={`${h.action} · ${h.actor ?? '—'}`} secondary={wWarszawie(h.at)} />
              </ListItem>
            ))}
          </List>
        </DialogContent>
      </Dialog>
    </Box>
  );
}

/**
 * „SPRAWDŹ WIDOK UŻYTKOWNIKA" — woła resolver SYSTEMU GŁÓWNEGO (ten sam co aplikacja).
 * Panel nie liczy pierwszeństwa sam: własna kopia logiki z czasem zaczęłaby pokazywać co innego,
 * niż zobaczy uczeń.
 */
export function SprawdzWidokUzytkownika() {
  const { api } = useServices();
  const [fraza, setFraza] = useState('');
  const [wyniki, setWyniki] = useState<UczenKatalogu[]>([]);
  const [wybrany, setWybrany] = useState<UczenKatalogu | null>(null);
  const [podglad, setPodglad] = useState<PodgladOdbiorcy | null>(null);
  const [blad, setBlad] = useState<string | null>(null);

  useEffect(() => {
    const q = fraza.trim();
    if (q.length < 3) return;
    let aktualne = true;
    const t = setTimeout(() => {
      tresciApi.uczniowie(api, q)
        .then((w) => { if (aktualne) { setWyniki(w); setBlad(null); } })
        .catch((e: unknown) => { if (aktualne) setBlad(komunikatBledu(e)); });
    }, 300);
    // Starsza odpowiedź (wolniejsza sieć) nie może nadpisać wyników nowszej frazy.
    return () => { aktualne = false; clearTimeout(t); };
  }, [api, fraza]);

  // „Na chwilę": pusto = teraz; inaczej czas Warszawy z pola daty → UTC (DST jawnie, jak w harmonogramie).
  const [chwila, setChwila] = useState('');
  const chwilaUtc = (): { ok: true; at?: string } | { ok: false; powod: string } => {
    if (!chwila) return { ok: true };
    const lokalny = zPolaDaty(chwila);
    if (!lokalny) return { ok: false, powod: 'Niepełna data i godzina.' };
    const k = warszawaNaUtc(lokalny);
    if (k.rodzaj === 'NIEISTNIEJACA') return { ok: false, powod: 'Tej godziny nie ma w Warszawie (zmiana czasu na letni).' };
    // Godzina podwójna (jesień): bierzemy pierwszą (czas letni) — podgląd mówi, którą chwilę sprawdził.
    return { ok: true, at: (k.rodzaj === 'JEDNOZNACZNA' ? k.utc : k.letnia).toISOString() };
  };

  const zapytanie = useRef(0);
  const sprawdz = (u: UczenKatalogu | null) => {
    const c = chwilaUtc();
    if (!c.ok) { setBlad(c.powod); return; }
    setWybrany(u);
    const nr = ++zapytanie.current;
    tresciApi.podgladOdbiorcy(api, u?.userId ?? null, c.at)
      .then((p) => { if (nr === zapytanie.current) { setPodglad(p); setBlad(null); } })
      .catch((e: unknown) => { if (nr === zapytanie.current) setBlad(komunikatBledu(e)); });
  };

  return (
    <Box>
      {blad ? <Alert severity="error" role="alert" sx={{ mb: 1 }}>{blad}</Alert> : null}
      <Stack direction="row" spacing={1} useFlexGap sx={{ mb: 1, flexWrap: 'wrap' }}>
        <TextField size="small" label="Uczeń (min. 3 znaki)" value={fraza} onChange={(e) => setFraza(e.target.value)}
          helperText="Imię, inicjał, e-mail albo szkoła — uczeń szkoły / miasta = dowolny uczeń z tej szkoły / miasta" />
        <TextField size="small" type="datetime-local" label="Na chwilę (Warszawa; puste = teraz)" value={chwila}
          onChange={(e) => setChwila(e.target.value)} slotProps={{ inputLabel: { shrink: true } }} />
        <Button size="small" onClick={() => sprawdz(null)}>Jak gość</Button>
      </Stack>
      <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap', mb: 1 }}>
        {(fraza.trim().length >= 3 ? wyniki : []).map((u) => (
          <Chip key={u.userId} variant="outlined" onClick={() => sprawdz(u)}
            label={`${u.displayName} · ${u.maskedEmail ?? '—'} · ${u.schoolName ?? 'bez szkoły'} · ${u.cityName ?? 'bez miasta'}`} />
        ))}
      </Stack>
      {podglad ? (
        <Box data-testid="widok-uzytkownika">
          <Typography variant="subtitle2">
            {wybrany ? `${wybrany.displayName} (${wybrany.schoolName ?? 'bez szkoły'}, ${wybrany.cityName ?? 'bez miasta'})` : 'Gość'}
            {' '}{chwila ? `${wWarszawie(podglad.at)} ZOBACZY:` : 'TERAZ ZOBACZY:'}
          </Typography>
          <Typography sx={{ fontWeight: 600, my: 0.5 }}>{podglad.final ? podglad.final.message : 'zwykły pasek aplikacji'}</Typography>
          <Typography variant="body2" data-testid="widok-zwyciezca">
            DLACZEGO: {podglad.winner
              ? `${podglad.winner.scope} #${podglad.winner.id} (priorytet ${podglad.winner.priority})`
              : 'żadna reguła nie pasuje — zwykły pasek'}
          </Typography>
          <Typography variant="caption" component="div" sx={{ color: 'text.secondary' }}>
            Kontekst resolvera: szkoła {podglad.schoolId !== null ? `#${podglad.schoolId}` : '—'} · miasto {podglad.cityId !== null ? `#${podglad.cityId}` : '—'}
            {' '}· {podglad.reason}
          </Typography>
          {podglad.losers.length > 0 ? (
            <List dense aria-label="Reguły przesłonięte">
              {podglad.losers.map((l) => (
                <ListItem key={l.id}><ListItemText primary={`${l.scope} #${l.id} — ${l.message}`} secondary={l.reason} /></ListItem>
              ))}
            </List>
          ) : null}
        </Box>
      ) : null}
    </Box>
  );
}
